/* eslint-disable camelcase */
import { useFormik } from 'formik';
import React from 'react';
import AddressLookup from '../form-elements/AddressLookup';
import CheckboxGroup from '../form-elements/CheckboxGroup';
import Form from '../form-elements/Form';
import Legend from '../form-elements/Legend';
import Required from '../form-elements/Required';
import Select from '../form-elements/Select';
import SuccessModal from '../form-elements/SuccessModal';
import TextField from '../form-elements/TextField';
import LoadingSpinner from '../LoadingSpinner';
import useBrochureForm from './hooks/useBrochureForm';

const BrochureForm = () => {
  const {
    validationSchema,
    initialValues,
    disable,
    postForm,
    fields,
    showLocations,
    setShowLocations,
    handleBrochureChange,
    handlePickLocation,
    submitInfo,
    showSuccessDialog,
    setShowSuccessDialog,
    handleReCaptchaVerify,
    addressSuggestions,
    handleAddressClick,
    addressLookup,
    setAddressLookup,
    waiting,
  } = useBrochureForm();

  const {
    handleBlur,
    touched,
    values,
    errors,
    setFieldValue,
    setTouched,
    submitForm,
    setValues,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async () => {
      const recaptchaToken = await handleReCaptchaVerify();

      const formData = {
        ...values,
        'g-recaptcha-response': recaptchaToken,
      };

      await postForm(formData, recaptchaToken, () => {
        setValues(initialValues);

        setTouched({});

        // post to data layer
        const {
          opt_in,
          brochures,
          first_name,
          last_name,
          phone,
          email,
          how_hear,
          address_line_1,
          address_line_2,
          county,
          postcode,
          current_page,
          referrer,
        } = values;

        // set email for DotDigital WBT
        window.dmPt('identify', email);

        const dataObj = {
          event: '2024_form_submit',
          data: {
            eventLabel: 'Brochure',
            formType: 'brochure',
            marketingConsent: opt_in,
            brochures,
            firstName: first_name,
            lastName: last_name,
            phone,
            email,
            howHear: how_hear,
            addressOne: address_line_1,
            addressTwo: address_line_2,
            county,
            postcode,
            currentPage: current_page,
            referrer: referrer,
          },
        };

        window.dataLayer.push(dataObj);
      });
    },
    enableReinitialize: true,
  });

  const {
    brochures,
    first_name,
    last_name,
    phone,
    how_hear,
    address_line_1,
    address_line_2,
    town,
    county,
    postcode,
    email,
    opt_in,
    honeypot,
    current_page,
    referrer,
    client_id,
  } = values;

  if (waiting) {
    return <LoadingSpinner />;
  } else {
    const { form, intro, thank_you } = fields;

    const {
      brochures: brochuresMeta,
      first_name: firstNameMeta,
      last_name: lastNameMeta,
      phone: phoneMeta,
      how_hear: howHearMeta,
      start_address: startAddressMeta,
      address_line_1: addressLine1Meta,
      address_line_2: addressLine2Meta,
      town: townMeta,
      county: countyMeta,
      postcode: postcodeMeta,
      email: emailMeta,
      opt_in: optInMeta,
      submit: submitMeta,
      gdpr: gdprMeta,
    } = form;

    return (
      <>
        <section className="c-form c-form--brochure o-wrapper u-mb-4">
          <div className="c-form__main">
            <div className="c-intro__content">
              <div className="c-intro__content__text">
                <h1 className="c-intro__content__text__title u-color--purple u-mb-7">
                  {intro.title}
                </h1>

                <h1
                  className="c-intro__content__text__title u-color--purple u-mb-5"
                  dangerouslySetInnerHTML={{ __html: intro.content }}
                ></h1>
              </div>
            </div>

            <Form
              autoComplete={false}
              className="c-form__body"
              onSubmit={(e) => {
                e.preventDefault();
                submitForm();
              }}
            >
              <fieldset className="c-form__fieldset c-form__fieldset--brochure">
                <Legend>Brochures</Legend>

                <div className="c-form__field-wrapper">
                  <div className="c-form__item c-form__item--checkbox-group">
                    <CheckboxGroup
                      name="brochures"
                      options={brochuresMeta.options}
                      label=""
                      handleChange={(value) =>
                        handleBrochureChange(value, brochures, setFieldValue)
                      }
                      selectedCheckboxes={brochures}
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="c-form__fieldset c-form__fieldset--name u-mb-19">
                <Legend>Name</Legend>

                <div className="c-form__field-wrapper">
                  <label className="u-a11y">
                    Honeypot
                    <input
                      name="honeypot"
                      type="hidden"
                      onChange={() => setFieldValue('honeypot', true)}
                      value={honeypot}
                    />
                  </label>

                  <input name="referrer" value={referrer} type="hidden" />
                  <input name="client_id" value={client_id} type="hidden" />
                  <input
                    name="current_page"
                    value={current_page}
                    type="hidden"
                  />

                  <div className="c-form__item c-form__item--first-name">
                    <TextField
                      label={
                        <>
                          {firstNameMeta.label}
                          {firstNameMeta.required && <Required />}
                        </>
                      }
                      id={firstNameMeta.id}
                      name={firstNameMeta.id}
                      placeholder={firstNameMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) =>
                        setFieldValue('first_name', value)
                      }
                      value={first_name}
                      validationError={
                        errors.first_name && touched[firstNameMeta.id]
                      }
                      validationMessage={errors.first_name}
                    />
                  </div>

                  <div className="c-form__item c-form__item--last-name">
                    <TextField
                      label={
                        <>
                          {lastNameMeta.label}
                          {lastNameMeta.required && <Required />}
                        </>
                      }
                      name={lastNameMeta.id}
                      id={lastNameMeta.id}
                      placeholder={lastNameMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) =>
                        setFieldValue('last_name', value)
                      }
                      value={last_name}
                      validationError={
                        errors.last_name && touched[lastNameMeta.id]
                      }
                      validationMessage={errors.last_name}
                    />
                  </div>

                  <div className="c-form__item c-form__item--phone">
                    <TextField
                      label={
                        <>
                          {phoneMeta.label}
                          {phoneMeta.required && <Required />}
                        </>
                      }
                      name={phoneMeta.id}
                      id={phoneMeta.id}
                      type="tel"
                      placeholder={phoneMeta.placeholder}
                      additionalMessage={phoneMeta.additional_message}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('phone', value)}
                      value={phone}
                      validationError={errors.phone && touched[phoneMeta.id]}
                      validationMessage={errors.phone}
                    />
                  </div>

                  <div className="c-form__item c-form__item--hear">
                    <Select
                      options={howHearMeta.options.filter(
                        ({ disabled }) => !disabled
                      )}
                      value={how_hear}
                      name={howHearMeta.id}
                      id={howHearMeta.id}
                      label={
                        <>
                          {howHearMeta.label}
                          {howHearMeta.required && <Required />}
                        </>
                      }
                      placeholderMessage={howHearMeta.placeholder_option}
                      handleChange={(value) => setFieldValue('how_hear', value)}
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="c-form__fieldset c-form__fieldset--address u-mb-11">
                <Legend>Your Address</Legend>

                <p className="u-weight--semibold u-mb-5">Your Address</p>

                <div className="c-form__field-wrapper">
                  <div className="c-form__item c-form__item--start-address">
                    <AddressLookup
                      label={
                        <>
                          {startAddressMeta.label}
                          {startAddressMeta.required && <Required />}
                        </>
                      }
                      name={startAddressMeta.id}
                      placeholder={startAddressMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setAddressLookup(value)}
                      value={addressLookup}
                      addresses={addressSuggestions}
                      handleAddressClick={(address) =>
                        handleAddressClick(address, setFieldValue)
                      }
                    />
                  </div>

                  <div className="c-form__item c-form__item--address-line-1">
                    <TextField
                      label={
                        <>
                          {addressLine1Meta.label}
                          {addressLine1Meta.required && <Required />}
                        </>
                      }
                      name={addressLine1Meta.id}
                      id={addressLine1Meta.id}
                      placeholder={addressLine1Meta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) =>
                        setFieldValue('address_line_1', value)
                      }
                      value={address_line_1}
                      validationError={
                        errors.address_line_1 && touched[addressLine1Meta.id]
                      }
                      validationMessage={errors.address_line_1}
                    />
                  </div>

                  <div className="c-form__item c-form__item--address-line-2">
                    <TextField
                      label={
                        <>
                          {addressLine2Meta.label}
                          {addressLine2Meta.required && <Required />}
                        </>
                      }
                      name={addressLine2Meta.id}
                      id={addressLine2Meta.id}
                      placeholder={addressLine2Meta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) =>
                        setFieldValue('address_line_2', value)
                      }
                      value={address_line_2}
                      validationError={
                        errors.address_line_2 && touched[addressLine2Meta.id]
                      }
                      validationMessage={errors.address_line_2}
                    />
                  </div>

                  <div className="c-form__item c-form__item--town">
                    <TextField
                      label={
                        <>
                          {townMeta.label}
                          {townMeta.required && <Required />}
                        </>
                      }
                      name={townMeta.id}
                      id={townMeta.id}
                      placeholder={townMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('town', value)}
                      value={town}
                      validationError={errors.town && touched[townMeta.id]}
                      validationMessage={errors.town}
                    />
                  </div>

                  <div className="c-form__item c-form__item--county">
                    <TextField
                      label={<>{countyMeta.label}</>}
                      name={countyMeta.id}
                      id={countyMeta.id}
                      placeholder={countyMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('county', value)}
                      value={county}
                      validationError={errors.county && touched[countyMeta.id]}
                      validationMessage={errors.county}
                    />
                  </div>

                  <div className="c-form__item c-form__item--postcode">
                    <TextField
                      label={
                        <>
                          {postcodeMeta.label}
                          {postcodeMeta.required && <Required />}
                        </>
                      }
                      name={postcodeMeta.id}
                      id={postcodeMeta.id}
                      placeholder={postcodeMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('postcode', value)}
                      value={postcode}
                      validationError={
                        errors.postcodeMeta && touched[postcodeMeta.id]
                      }
                      validationMessage={errors.postcodeMeta}
                    />
                  </div>

                  <div className="c-form__item c-form__item--email">
                    <TextField
                      label={
                        <>
                          {emailMeta.label}
                          {emailMeta.required && <Required />}
                        </>
                      }
                      name={emailMeta.id}
                      id={emailMeta.id}
                      type="email"
                      placeholder={emailMeta.placeholder}
                      onBlur={handleBlur}
                      handleChange={(value) => setFieldValue('email', value)}
                      value={email}
                      validationError={errors.email && touched[emailMeta.id]}
                      validationMessage={errors.email}
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="c-form__item c-form__item--opt-in u-mb-3">
                <Legend>Opt in</Legend>

                <label htmlFor="opt_in" className="c-form__checkbox">
                  <input
                    id="opt_in"
                    value="opt_in"
                    type="checkbox"
                    name="opt_in"
                    onChange={() => setFieldValue('opt_in', !opt_in)}
                    checked={opt_in}
                  />
                  <span></span>
                </label>

                <div>
                  <p className="u-weight--semibold u-mb-0">
                    {optInMeta.options[0].display}
                  </p>

                  <p
                    className="c-form__small"
                    dangerouslySetInnerHTML={{
                      __html: optInMeta.options[0].info,
                    }}
                  ></p>
                </div>
              </fieldset>

              <div className="c-form__item c-form__item--gdpr-no-checkbox u-mb-3">
                <div>
                  <p className="u-weight--semibold u-mb-0">
                    {gdprMeta.display}
                  </p>

                  <p
                    className="c-form__small"
                    dangerouslySetInnerHTML={{
                      __html: gdprMeta.info,
                    }}
                  ></p>
                </div>
              </div>

              <div className="c-form__submit">
                <button
                  type="submit"
                  className="o-btn"
                  disabled={disable(errors, values, initialValues)}
                >
                  {submitMeta.label}
                </button>
              </div>
            </Form>
          </div>
        </section>

        {showSuccessDialog && (
          <SuccessModal thank_you={thank_you} setShow={setShowSuccessDialog} />
        )}
      </>
    );
  }
};

export default BrochureForm;
